import { useState } from "react";
import { Heading } from "@made-people/blueprint-heading";
import {
  InputField,
  Form,
  SelectBox,
  SelectBoxItem,
} from "@made-people/blueprint-form";
import { ButtonAsButton } from "@made-people/blueprint-button";
import { useTranslate } from "@made-people/grease-translations";
import {
  useAccountState,
  useAccountUpdateCustomer,
} from "@made-people/grease-account";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";

type DetailsFormData = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  street: string;
  zipCode: string;
  city: string;
  country: string;
};

const emptyFormData: DetailsFormData = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  street: "",
  zipCode: "",
  city: "",
  country: "",
};

type DisplayField = {
  key: string;
  label: string;
  editable: false;
};

type EditableField = {
  key: keyof DetailsFormData;
  label: string;
  editable: true;
  type?: "tel";
};

type FieldConfig = DisplayField | EditableField;

const fieldConfig: FieldConfig[] = [
  { key: "email", label: "Checkout_Adress_Label_Email", editable: false },
  {
    key: "firstName",
    label: "Checkout_Adress_Label_FirstName",
    editable: true,
  },
  { key: "lastName", label: "Checkout_Adress_Label_LastName", editable: true },
  {
    key: "phoneNumber",
    label: "Checkout_Adress_Label_PhoneNumber",
    editable: true,
    type: "tel",
  },
  { key: "street", label: "Checkout_Adress_Label_Street", editable: true },
  { key: "zipCode", label: "Checkout_Adress_Label_Zip", editable: true },
  { key: "city", label: "Checkout_Adress_Label_City", editable: true },
  { key: "country", label: "Form_Field_Country", editable: true },
];

export const MissMaryAccountDetails = () => {
  const translate = useTranslate();
  const account = useAccountState();
  const updateCustomer = useAccountUpdateCustomer();
  const { countries } = useCountriesAndLanguages();
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<DetailsFormData>(emptyFormData);

  const customer = account?.customer;

  if (!customer) return null;

  const address = customer.addresses[0];

  const displayValues: Record<string, string> = {
    email: customer.email,
    firstName: customer.firstName || "",
    lastName: customer.lastName || "",
    phoneNumber: address?.phoneNumber || "",
    street: address?.street || "",
    zipCode: address?.zipCode || "",
    city: address?.city || "",
    country: address?.country || "",
  };

  const updateField = (field: keyof DetailsFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEdit = () => {
    setFormData({
      firstName: customer.firstName || "",
      lastName: customer.lastName || "",
      phoneNumber: address?.phoneNumber || "",
      street: address?.street || "",
      zipCode: address?.zipCode || "",
      city: address?.city || "",
      country: address?.country || "",
    });
    setIsEditing(true);
    setError("");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError("");
  };

  const handleSave = async () => {
    setError("");
    const existingAddress = customer.addresses[0];
    const updatedAddress = {
      ...(existingAddress || { country: "" }),
      phoneNumber: formData.phoneNumber,
      street: formData.street,
      zipCode: formData.zipCode,
      city: formData.city,
      country: formData.country,
    };

    const updatedAddresses =
      customer.addresses.length > 0
        ? [updatedAddress, ...customer.addresses.slice(1)]
        : [updatedAddress];

    const result = await updateCustomer.execute({
      customer: {
        ...customer,
        firstName: formData.firstName,
        lastName: formData.lastName,
        addresses: updatedAddresses,
      },
    });

    if (result.success) {
      setIsEditing(false);
    } else {
      setError(translate(result.error));
    }
  };

  return (
    <div className="w-full pb-10">
      <Heading
        heading={translate("Account_Details_Heading")}
        headingTag="h2"
        variants={{ headingSize: { initial: "sm", lg: "md" } }}
        className="pb-4 md:pb-6 hidden lg:block"
      />

      <div className="p-6 rounded-md bg-[#f3f2ef]">
        {isEditing ? (
          <div className="flex flex-col gap-6 lg:max-w-[50%]">
            <h3 className="text-label-md uppercase hidden lg:block">
              {translate("Account_Details_SectionHeading")}
            </h3>
            <Form>
              <div className="flex flex-col gap-5">
                {fieldConfig.map((field) =>
                  field.editable ? (
                    field.key === "country" ? (
                      <SelectBox
                        key={field.key}
                        variants={{
                          size: "sm",
                        }}
                        label={translate(field.label)}
                        value={formData.country}
                        onChange={(value) => updateField("country", value)}
                      >
                        {countries.map((country) => (
                          <SelectBoxItem
                            key={country.countryCode}
                            value={country.countryCode}
                            label={country.name}
                          />
                        ))}
                      </SelectBox>
                    ) : (
                      <InputField
                        key={field.key}
                        id={`details-${field.key}`}
                        template="floating-label"
                        type={field.type || "text"}
                        placeholder={translate(field.label)}
                        value={formData[field.key]}
                        onChange={(e) => updateField(field.key, e.target.value)}
                      />
                    )
                  ) : (
                    <div key={field.key} className="flex flex-col gap-2">
                      <span className="text-label-sm uppercase tracking-wider">
                        {translate(field.label)}
                      </span>
                      <span className="text-body-lg tracking-tight">
                        {displayValues[field.key]}
                      </span>
                    </div>
                  ),
                )}
                {error && (
                  <p role="alert" className="text-sm text-system-error">
                    {error}
                  </p>
                )}
                <div className="flex gap-4 items-center">
                  <ButtonAsButton
                    label={translate("Account_Details_Cancel")}
                    onClick={handleCancel}
                    variants={{ style: "outline", width: "auto" }}
                  />
                  <ButtonAsButton
                    label={translate("Account_Details_Save")}
                    onClick={handleSave}
                    variants={{ width: "full" }}
                    progress={updateCustomer.loadingState}
                  />
                </div>
              </div>
            </Form>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <h3 className="text-label-md uppercase hidden lg:block">
              {translate("Account_Details_SectionHeading")}
            </h3>
            <div className="flex flex-col gap-6">
              {fieldConfig.map((field) => (
                <div key={field.key} className="flex flex-col gap-2">
                  <span className="text-label-sm uppercase tracking-wider font-medium">
                    {translate(field.label)}
                  </span>
                  <span className="text-body-lg tracking-tight">
                    {displayValues[field.key] || "\u2014"}
                  </span>
                </div>
              ))}
            </div>
            <div className="md:max-w-[348px]">
              <ButtonAsButton
                label={translate("Account_Details_Edit")}
                onClick={handleEdit}
                variants={{ style: "outline", width: "full" }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
